import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  alpha,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CalendarMonth as CalendarIcon,
  DirectionsCar as VehicleIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  ArrowDownward as EarlyIcon,
  ArrowUpward as LateIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import isoWeek from 'dayjs/plugin/isoWeek';
import { 
  fetchPlannedShifts, 
  cancelPlannedShift,
  deletePlannedShift,
  fetchActualShiftsForComparison 
} from '../../redux/slices/plannedShiftsSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

dayjs.extend(isoWeek);
dayjs.locale('de');

// Constants
const HOUR_WIDTH = 120;
const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 50;
const ROLLING_HOURS_DISPLAY = 13; // Show 1 past hour + 12 future hours

// Shift definitions for vehicle availability
const SHIFT_DEFINITIONS = {
  '8h': {
    early: { start: 6, end: 14, label: 'Frühschicht' },
    late: { start: 14, end: 22, label: 'Spätschicht' },
    night: { start: 22, end: 6, label: 'Nachtschicht', overnight: true }
  },
  '12h': {
    day: { start: 6, end: 18, label: 'Tagschicht' },
    night: { start: 18, end: 6, label: 'Nachtschicht', overnight: true }
  }
};

const ShiftPlanner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  const { plannedShifts, actualShifts, isLoading, error } = useSelector(state => state.plannedShifts);
  const { drivers } = useSelector(state => state.drivers);
  const { vehicles } = useSelector(state => state.vehicles);
  const { shifts: appDataShifts } = useSelector(state => state.appData); // Get shifts from AppDataManager
  
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [isRollingView, setIsRollingView] = useState(true); // Rolling 24h calendar
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [displayMode, setDisplayMode] = useState('drivers'); // 'drivers' or 'vehicles'
  const [shiftDuration, setShiftDuration] = useState('12h'); // '8h' or '12h'
  
  // Context menu and delete dialog states
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState('single'); // 'single' or 'all'
  
  // Check if current date is today
  const isToday = currentDate.isSame(dayjs(), 'day');
  
  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
  }, [dispatch]);

  // Update current time every minute
  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(dayjs());
    };
    
    // Update immediately
    updateTime();
    
    // Set up interval to update every minute
    const interval = setInterval(updateTime, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Fetch planned shifts based on view
  useEffect(() => {
    let startDate, endDate;
    
    if (isRollingView && isToday && viewMode === 'day') {
      // For rolling view on today, fetch shifts from yesterday to tomorrow to ensure we have all shifts that might appear
      startDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      endDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
    } else {
      // Original logic for non-rolling view
      startDate = viewMode === 'week' 
        ? currentDate.startOf('isoWeek').format('YYYY-MM-DD')
        : currentDate.format('YYYY-MM-DD');
        
      endDate = viewMode === 'week'
        ? currentDate.endOf('isoWeek').format('YYYY-MM-DD')
        : currentDate.format('YYYY-MM-DD');
    }
    
    const params = {
      startDate,
      endDate
    };
    
    if (selectedDriver !== 'all') {
      params.driverId = selectedDriver;
    }
    
    dispatch(fetchPlannedShifts(params));
    
    // Fetch actual shifts for comparison
    if (viewMode === 'day') {
      dispatch(fetchActualShiftsForComparison({ date: currentDate.format('YYYY-MM-DD') }));
    } else if (viewMode === 'week') {
      // Also fetch actual shifts for today in week view to check for unstarted shifts
      dispatch(fetchActualShiftsForComparison({ date: dayjs().format('YYYY-MM-DD') }));
    }
  }, [dispatch, currentDate, viewMode, selectedDriver, isRollingView, isToday]);
  
  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };
  
  const handleDateChange = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(prev => direction === 'next' ? prev.add(1, 'week') : prev.subtract(1, 'week'));
    } else {
      setCurrentDate(prev => direction === 'next' ? prev.add(1, 'day') : prev.subtract(1, 'day'));
    }
  };
  
  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleDisplayModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDisplayMode(newMode);
    }
  };


  const handleShiftDurationChange = (event, newDuration) => {
    if (newDuration !== null) {
      setShiftDuration(newDuration);
    }
  };
  
  
  const handleEditShift = (shiftId) => {
    // Don't allow editing of unplanned shifts
    if (shiftId && shiftId.startsWith('unplanned-')) {
      console.log('Ungeplante Schichten können nicht bearbeitet werden');
      return;
    }
    navigate(`/schichtplaner/${shiftId}/bearbeiten`);
  };
  
  
  // Context menu handlers
  const handleContextMenu = (event, shift) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setSelectedShift(shift);
  };
  
  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setSelectedShift(null);
  };
  
  const handleEditFromMenu = () => {
    if (selectedShift) {
      handleEditShift(selectedShift.id);
    }
    handleCloseContextMenu();
  };
  
  const handleDeleteFromMenu = () => {
    if (selectedShift) {
      setDeleteDialogOpen(true);
      // Close context menu but keep selectedShift for the dialog
      setContextMenuAnchor(null);
    } else {
      handleCloseContextMenu();
    }
  };
  
  // Delete dialog handlers
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedShift(null);
    setDeleteOption('single');
  };
  
  const handleConfirmDelete = async () => {
    if (!selectedShift || !selectedShift.id) return;
    
    try {
      if (deleteOption === 'single') {
        // Delete only the current date
        const datesToDelete = [selectedShift.displayDate || selectedShift.date];
        await dispatch(cancelPlannedShift({ id: selectedShift.id, cancelDates: datesToDelete }));
      } else {
        // Delete all dates (entire shift)
        await dispatch(deletePlannedShift(selectedShift.id));
      }
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  };
  
  // Get the original dates array for a shift
  const getOriginalDates = (shift) => {
    if (!shift) return [];
    
    // Try different sources for dates
    if (shift.originalDates && Array.isArray(shift.originalDates)) {
      return shift.originalDates;
    }
    
    if (shift.dates && Array.isArray(shift.dates)) {
      return shift.dates;
    }
    
    // Find original shift by ID
    if (shift.id && plannedShifts && plannedShifts.length > 0) {
      const originalShift = plannedShifts.find(s => s.id === shift.id);
      if (originalShift && originalShift.dates && Array.isArray(originalShift.dates)) {
        return originalShift.dates;
      }
    }
    
    return [];
  };

  // Check if shift has multiple dates
  const hasMultipleDates = (shift) => {
    const dates = getOriginalDates(shift);
    return dates.length > 1;
  };
  
  // Get shifts for current view
  const getShiftsForDisplay = () => {
    const shiftsMap = new Map();
    
    // First, process all planned shifts
    plannedShifts.forEach(shift => {
      if (shift.status === 'cancelled') return;
      
      // Collect all relevant dates for this shift (start dates + end dates for overnight shifts)
      const relevantDates = [];
      
      // Add all start dates
      shift.dates.forEach(date => {
        relevantDates.push({
          date,
          isStartDate: true,
          isEndDate: false
        });
      });
      
      // Add end dates for overnight shifts
      if (shift.isOvernightShift && shift.dateEndMapping) {
        Object.values(shift.dateEndMapping).forEach(endDate => {
          // Only add if it's not already included as a start date
          if (!shift.dates.includes(endDate)) {
            relevantDates.push({
              date: endDate,
              isStartDate: false,
              isEndDate: true
            });
          }
        });
      }
      
      // Process each relevant date
      relevantDates.forEach(({ date, isEndDate }) => {
        const shiftDate = dayjs(date);
        
        // For end dates, we need to find the original start date to calculate correct times
        let actualStartTime, actualEndTime, originalStartDate;
        
        if (isEndDate) {
          // Find the original start date for this end date
          const originalStartDateStr = Object.keys(shift.dateEndMapping || {}).find(
            startDate => shift.dateEndMapping[startDate] === date
          );
          
          if (originalStartDateStr) {
            originalStartDate = dayjs(originalStartDateStr);
            const [hours, minutes] = shift.startTime.split(':');
            const originalStartTime = originalStartDate.hour(parseInt(hours)).minute(parseInt(minutes));
            originalStartTime.add(shift.plannedDuration, 'hour');
            
            // Calculate how many hours were worked on the previous day
            const hoursWorkedPreviousDay = 24 - originalStartTime.hour() - (originalStartTime.minute() / 60);
            const remainingHours = shift.plannedDuration - hoursWorkedPreviousDay;
            
            // For end dates, the displayed shift should be from midnight to the calculated remaining time
            actualStartTime = shiftDate.hour(0).minute(0); // Start at midnight on end date
            actualEndTime = actualStartTime.add(remainingHours, 'hour'); // End after remaining hours
          } else {
            return; // Skip if we can't find the original start date
          }
        } else {
          // For start dates, calculate normally
          originalStartDate = shiftDate;
          const [hours, minutes] = shift.startTime.split(':');
          actualStartTime = shiftDate.hour(parseInt(hours)).minute(parseInt(minutes));
          actualEndTime = actualStartTime.add(shift.plannedDuration, 'hour');
          
          // For overnight shifts on start dates, truncate at midnight
          if (shift.isOvernightShift && actualEndTime.isAfter(actualStartTime, 'day')) {
            actualEndTime = shiftDate.hour(23).minute(59).second(59); // End at end of day
          }
        }
        
        // Use the calculated times for filtering
        const startTime = actualStartTime;
        const endTime = actualEndTime;
        
        // Filter shifts based on view mode and rolling view setting
        if (viewMode === 'day') {
          if (isRollingView && isToday) {
            // For rolling view on today, check if shift is within the visible time window
            const viewStart = currentTime.subtract(1, 'hour');
            const viewEnd = currentTime.add(ROLLING_HOURS_DISPLAY - 1, 'hour');
            
            // For overnight shifts, we need to check if any part of the shift is visible
            // Include shifts that overlap with the rolling window
            if (endTime.isBefore(viewStart) || startTime.isAfter(viewEnd)) {
              return;
            }
          } else {
            // For non-rolling day view, check if the current date matches the selected day
            if (!shiftDate.isSame(currentDate, 'day')) return;
          }
        } else if (viewMode === 'week') {
          // For week view, check if the current date is in the selected week
          if (!shiftDate.isSame(currentDate, 'isoWeek')) return;
        }
        
        const key = `${shift.driverId}-${date}-${isEndDate ? 'end' : 'start'}`;
        
        // Try to get from actualShifts first, then from appDataShifts
        // For end dates, use the original start date to find the actual shift
        const actualShiftDate = isEndDate ? originalStartDate.format('YYYY-MM-DD') : date;
        let actualShift = actualShifts[actualShiftDate]?.drivers?.[shift.driverId] || null;
        
        // If not found in actualShifts, check appDataShifts
        if (!actualShift && appDataShifts.drivers && appDataShifts.drivers[shift.driverId]) {
          // Get the driver shift data directly - it's not organized by date
          actualShift = appDataShifts.drivers[shift.driverId];
        }
        
        const shiftObject = {
          ...shift,
          date: originalStartDate.format('YYYY-MM-DD'), // Use original start date for shift data
          displayDate: date, // Keep display date for positioning
          startTime,
          endTime,
          actualShift,
          isEndDateDisplay: isEndDate, // Flag to know this is an end date display
          originalStartTimeString: shift.startTime, // Keep original string for UI display
          originalDates: shift.dates // Keep original dates array for multi-day detection
        };
        
        
        shiftsMap.set(key, shiftObject);
      });
    });

    // Then, process unplanned shifts from actualShifts and appDataShifts
    const processUnplannedShifts = (shiftData, source) => {
      if (!shiftData || !shiftData.drivers) {
        return;
      }
      
      Object.entries(shiftData.drivers).forEach(([driverId, driverShiftData]) => {
        if (!driverShiftData || !driverShiftData.shifts) {
          return;
        }
        
        // Find the driver info
        const driver = drivers.find(d => d.id === driverId);
        if (!driver) return;
        
        // Find the assigned vehicle for this driver
        // Find vehicle assigned to this driver using correct field structure
        const assignedVehicle = vehicles.find(v => v.current_driver?.id === driverId);
        
        const vehicleId = assignedVehicle?.id || null;
        const vehiclePlate = assignedVehicle?.licensePlate || null;
        
        // Process each shift for this driver
        driverShiftData.shifts.forEach((actualShift, shiftIndex) => {
          if (!actualShift.isActive && !actualShift.shiftStart) {
            return;
          }
          
          // Get shift start time
          let shiftStartTime;
          if (actualShift.shiftStart && actualShift.shiftStart.seconds) {
            shiftStartTime = dayjs.unix(actualShift.shiftStart.seconds);
          } else if (actualShift.startTime) {
            shiftStartTime = dayjs(actualShift.startTime);
          } else {
            return;
          }
          
          const shiftDate = shiftStartTime.format('YYYY-MM-DD');
          
          // Check if this is already covered by a planned shift
          const plannedShiftKey = `${driverId}-${shiftDate}-start`;
          if (shiftsMap.has(plannedShiftKey)) {
            return; // Skip if there's already a planned shift for this driver on this date
          }
          
          // Calculate end time (estimate 8 hours if no end time available)
          let shiftEndTime;
          if (actualShift.shiftEnd && actualShift.shiftEnd.seconds) {
            shiftEndTime = dayjs.unix(actualShift.shiftEnd.seconds);
          } else if (actualShift.endTime) {
            shiftEndTime = dayjs(actualShift.endTime);
          } else {
            // Estimate 8 hours for active shifts
            shiftEndTime = shiftStartTime.add(8, 'hour');
          }
          
          // Check if this shift should be displayed based on view settings
          const dayJsShiftDate = dayjs(shiftDate);
          
          // Filter based on view mode
          if (viewMode === 'day') {
            if (isRollingView && isToday) {
              // For rolling view on today, check if shift is within the visible time window
              const viewStart = currentTime.subtract(1, 'hour');
              const viewEnd = currentTime.add(ROLLING_HOURS_DISPLAY - 1, 'hour');
              
              if (shiftEndTime.isBefore(viewStart) || shiftStartTime.isAfter(viewEnd)) {
                return;
              }
            } else {
              // For non-rolling day view, check if the shift date matches the selected day
              if (!dayJsShiftDate.isSame(currentDate, 'day')) {
                return;
              }
            }
          } else if (viewMode === 'week') {
            // For week view, check if the shift date is in the selected week
            if (!dayJsShiftDate.isSame(currentDate, 'isoWeek')) {
              return;
            }
          }
          
          // Create unplanned shift object
          const unplannedShiftKey = `unplanned-${driverId}-${shiftDate}-${shiftIndex}`;
          const unplannedShift = {
            id: `unplanned-${driverId}-${shiftDate}-${shiftIndex}`,
            driverId,
            driverName: driver.user?.name || driver.name || 'Unbekannter Fahrer',
            vehicleId: vehicleId,
            vehiclePlate: vehiclePlate,
            dates: [shiftDate],
            startTime: shiftStartTime,
            endTime: shiftEndTime,
            originalStartTimeString: shiftStartTime.format('HH:mm'),
            plannedDuration: shiftEndTime.diff(shiftStartTime, 'hour', true),
            status: 'unplanned',
            isOvernightShift: false,
            isEndDateDisplay: false,
            actualShift: driverShiftData,
            date: shiftDate,
            displayDate: shiftDate,
            originalDates: [shiftDate],
            notes: 'Ungeplante Schicht',
            source: source // Track where this shift came from
          };
          
          shiftsMap.set(unplannedShiftKey, unplannedShift);
        });
      });
    };
    
    // Process unplanned shifts from actualShifts
    Object.entries(actualShifts).forEach(([date, shiftData]) => {
      // actualShifts has structure: {driverId: driverData} but we need {drivers: {driverId: driverData}}
      const wrappedShiftData = { drivers: shiftData };
      processUnplannedShifts(wrappedShiftData, `actualShifts-${date}`);
    });
    
    // Process unplanned shifts from appDataShifts 
    // FOR ROLLING VIEW: appDataShifts should be the primary source since it's live data
    if (appDataShifts) {
      processUnplannedShifts(appDataShifts, 'appDataShifts');
    }
    
    // Sort shifts by start time within each group
    const shiftsArray = Array.from(shiftsMap.values());
    return shiftsArray.sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
  };
  
  // Calculate shift position and width
  const calculateShiftStyle = (shift) => {
    const startHour = shift.startTime.hour() + shift.startTime.minute() / 60;
    
    // Calculate actual duration based on whether this is an end date display
    let duration;
    if (shift.isEndDateDisplay) {
      // For end date displays, calculate the remaining hours
      const originalStartDateStr = Object.keys(shift.dateEndMapping || {}).find(
        startDate => shift.dateEndMapping[startDate] === shift.displayDate
      );
      if (originalStartDateStr) {
        const [hours, minutes] = shift.originalStartTimeString.split(':');
        const originalStartTime = dayjs(originalStartDateStr).hour(parseInt(hours)).minute(parseInt(minutes));
        const hoursWorkedPreviousDay = 24 - originalStartTime.hour() - (originalStartTime.minute() / 60);
        duration = shift.plannedDuration - hoursWorkedPreviousDay;
      } else {
        duration = shift.plannedDuration;
      }
    } else {
      // For start date displays, use the time difference between start and end
      duration = shift.endTime.diff(shift.startTime, 'hour', true);
    }
    
    const timeSlots = getTimeSlots();
    
    if (timeSlots.length === 0) return { display: 'none' };
    
    // For rolling view with day mode on today, we need to handle shifts that span across days
    if (isRollingView && isToday && viewMode === 'day') {
      const firstSlotHour = timeSlots[0].actualHour;
      const lastSlotHour = timeSlots[timeSlots.length - 1].actualHour;
      const shiftStartHour = shift.startTime.hour() + shift.startTime.minute() / 60;
      const shiftEndHour = shiftStartHour + duration;
      
      // Check if shift overlaps with visible time window
      // For rolling view, we need to use the continuous hour values, not wrapped actualHour
      const viewStart = currentTime.subtract(1, 'hour').hour() + currentTime.subtract(1, 'hour').minute() / 60;
      const viewEnd = viewStart + ROLLING_HOURS_DISPLAY;
      
      if (shiftEndHour <= viewStart || shiftStartHour >= viewEnd) {
        return { display: 'none' };
      }
      
      // Calculate visible portion of the shift
      const visibleStartHour = Math.max(shiftStartHour, viewStart);
      const visibleEndHour = Math.min(shiftEndHour, viewEnd);
      const visibleDuration = visibleEndHour - visibleStartHour;
      
      // Find the slot index that matches the visible start hour
      let slotIndex = -1;
      const targetActualHour = Math.floor(visibleStartHour) % 24;
      
      for (let i = 0; i < timeSlots.length; i++) {
        if (timeSlots[i].actualHour === targetActualHour) {
          slotIndex = i;
          break;
        }
      }
      
      if (slotIndex === -1) {
        return { display: 'none' };
      }
      
      // Calculate position and width for visible portion
      const minuteOffset = (visibleStartHour % 1); // Decimal part represents minutes
      const leftPosition = (slotIndex + minuteOffset) * HOUR_WIDTH;
      const width = visibleDuration * HOUR_WIDTH;
      
      return {
        left: `${leftPosition}px`,
        width: `${Math.max(width - 4, 10)}px`, // Minimum width of 10px
        top: '2px',
        height: `${ROW_HEIGHT - 4}px`
      };
    }
    
    // Original logic for non-rolling view or week view
    const viewStartHour = timeSlots[0].hour;
    const viewEndHour = timeSlots[timeSlots.length - 1].hour + 1; // Add 1 to include the full last hour
    const totalViewHours = viewEndHour - viewStartHour;
    const displayStartHour = startHour - viewStartHour;
    const displayEndHour = displayStartHour + duration;
    
    // Hide shifts outside visible time range
    if (displayEndHour <= 0 || displayStartHour >= totalViewHours) {
      return { display: 'none' };
    }
    
    // Calculate actual visible portion of shift
    const visibleStartHour = Math.max(0, displayStartHour);
    const visibleEndHour = Math.min(totalViewHours, displayEndHour);
    const visibleDuration = visibleEndHour - visibleStartHour;
    
    if (visibleDuration <= 0) {
      return { display: 'none' };
    }
    
    return {
      left: `${visibleStartHour * HOUR_WIDTH}px`,
      width: `${visibleDuration * HOUR_WIDTH - 4}px`,
      top: '2px',
      height: `${ROW_HEIGHT - 4}px`
    };
  };
  
  // Get time slots for header
  const getTimeSlots = () => {
    // Only use rolling view if it's enabled AND we're viewing today
    if (isRollingView && isToday) {
      const currentHour = currentTime.hour();
      
      if (viewMode === 'day') {
        // Rolling 24h view starting from 1 hour before current time
        const startHour = Math.floor(currentHour - 1);
        const slots = [];
        
        for (let i = 0; i < ROLLING_HOURS_DISPLAY; i++) {
          const hour = (startHour + i + 24) % 24;
          const displayHour = startHour + i;
          slots.push({ 
            hour: displayHour, 
            actualHour: hour,
            label: `${hour.toString().padStart(2, '0')}:00`,
            isCurrent: hour === currentHour
          });
        }
        
        return slots;
      } else {
        // Week view: show full 24h for each day
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
          slots.push({ 
            hour, 
            actualHour: hour,
            label: `${hour.toString().padStart(2, '0')}:00`,
            isCurrent: currentDate.isSame(currentTime, 'day') && hour === currentHour
          });
        }
        return slots;
      }
    }
    
    // For fixed view (non-rolling or non-today), show all 24 hours
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      slots.push({ 
        hour, 
        actualHour: hour,
        label: `${hour.toString().padStart(2, '0')}:00`,
        isCurrent: false
      });
    }
    return slots;
  };
  
  // Group shifts by driver
  const groupShiftsByDriver = () => {
    const shifts = getShiftsForDisplay();
    const driverGroups = new Map();
    
    // Initialize groups for all drivers
    drivers.forEach(driver => {
      if (selectedDriver === 'all' || driver.id === selectedDriver) {
        driverGroups.set(driver.id, {
          driver,
          shifts: [],
          hasUnstartedShift: false
        });
      }
    });
    
    // Add shifts to groups and check for unstarted shifts
    const now = dayjs();
    shifts.forEach(shift => {
      if (driverGroups.has(shift.driverId)) {
        const group = driverGroups.get(shift.driverId);
        group.shifts.push(shift);
        
        // Check if this shift should have started but hasn't
        // Skip check for end date displays of overnight shifts (they already started on previous day)
        if (!shift.isEndDateDisplay) {
          const shiftDate = dayjs(shift.displayDate || shift.date);
          const shiftStartTime = shiftDate.hour(shift.startTime.hour()).minute(shift.startTime.minute());
          
          // Check if shift hasn't been started
          if (!shift.actualShift) {
            // Case 1: Regular shift on today - show warning if start time has passed
            if (shiftDate.isSame(now, 'day') && shiftStartTime.isBefore(now)) {
              group.hasUnstartedShift = true;
            }
            // Case 2: Overnight shift that started yesterday - show warning for entire next day (until 23:59)
            else if (shift.isOvernightShift && shiftDate.isSame(now.subtract(1, 'day'), 'day')) {
              // For overnight shifts that should have started yesterday, show warning all day today
              group.hasUnstartedShift = true;
            }
          }
        }
      }
    });
    
    return Array.from(driverGroups.values());
  };
  
  // Calculate current time marker position
  const getCurrentTimeMarkerStyle = () => {
    if (!isRollingView || !isToday || viewMode !== 'day') return { display: 'none' };
    
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { display: 'none' };
    
    const currentHour = currentTime.hour();
    const currentMinutes = currentTime.minute();
    
    // Find the exact slot that contains the current hour
    const currentSlotIndex = timeSlots.findIndex(slot => slot.actualHour === currentHour);
    if (currentSlotIndex === -1) return { display: 'none' };
    
    // Calculate position: slot index * width + minutes within that slot
    const minuteProgress = currentMinutes / 60; // 0-1 progress within the hour
    const leftPosition = (currentSlotIndex * HOUR_WIDTH) + (minuteProgress * HOUR_WIDTH) + 150; // +150 for driver column
    
    return {
      left: `${leftPosition}px`,
      top: '0px',
      bottom: '0px',
      width: '2px',
      position: 'absolute',
      backgroundColor: '#ff4444',
      zIndex: 1000,
      boxShadow: '0 0 4px rgba(255, 68, 68, 0.5)'
    };
  };

  // Calculate fleet utilization
  const calculateFleetUtilization = () => {
    const utilization = [];
    const timeSlots = getTimeSlots();
    const shifts = getShiftsForDisplay();
    
    for (let i = 0; i < timeSlots.length - 1; i++) {
      const hour = timeSlots[i].hour;
      const activeShifts = shifts.filter(shift => {
        const shiftStart = shift.startTime.hour() + shift.startTime.minute() / 60;
        const shiftEnd = shiftStart + shift.plannedDuration;
        return shiftStart <= hour && shiftEnd > hour;
      });
      
      const totalVehicles = vehicles.filter(v => v.status === 'active').length;
      const utilizedVehicles = activeShifts.length;
      const utilizationPercent = totalVehicles > 0 ? (utilizedVehicles / totalVehicles) * 100 : 0;
      
      utilization.push({
        hour,
        utilized: utilizedVehicles,
        total: totalVehicles,
        percent: utilizationPercent
      });
    }
    
    return utilization;
  };

  // Get week days for week view
  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf('isoWeek');
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.add(i, 'day');
      days.push({
        date: day,
        dateString: day.format('YYYY-MM-DD'),
        dayName: day.format('dddd'),
        dayShort: day.format('dd'),
        dayNumber: day.format('DD'),
        isToday: day.isSame(dayjs(), 'day')
      });
    }
    
    return days;
  };

  // Group shifts by driver and day for week view
  const getShiftsGroupedByDriverAndDay = () => {
    const shifts = getShiftsForDisplay();
    const weekDays = getWeekDays();
    const driverGroups = new Map();
    
    // Initialize groups for all drivers
    drivers.forEach(driver => {
      if (selectedDriver === 'all' || driver.id === selectedDriver) {
        const driverShifts = new Map();
        weekDays.forEach(day => {
          driverShifts.set(day.dateString, []);
        });
        
        driverGroups.set(driver.id, {
          driver,
          shifts: driverShifts,
          hasUnstartedShift: false
        });
      }
    });
    
    // Group shifts by driver and day
    const now = dayjs();
    shifts.forEach(shift => {
      if (driverGroups.has(shift.driverId)) {
        const group = driverGroups.get(shift.driverId);
        const shiftDateString = dayjs(shift.displayDate || shift.date).format('YYYY-MM-DD');
        
        if (group.shifts.has(shiftDateString)) {
          group.shifts.get(shiftDateString).push(shift);
          
          // Check if this shift should have started but hasn't
          // Skip check for end date displays of overnight shifts (they already started on previous day)
          if (!shift.isEndDateDisplay) {
            const shiftDate = dayjs(shift.displayDate || shift.date);
            const shiftStartTime = shiftDate.hour(shift.startTime.hour()).minute(shift.startTime.minute());
            
            // Check if shift hasn't been started
            if (!shift.actualShift) {
              // Case 1: Regular shift on today - show warning if start time has passed
              if (shiftDate.isSame(now, 'day') && shiftStartTime.isBefore(now)) {
                group.hasUnstartedShift = true;
              }
              // Case 2: Overnight shift that started yesterday - show warning for entire next day (until 23:59)
              else if (shift.isOvernightShift && shiftDate.isSame(now.subtract(1, 'day'), 'day')) {
                // For overnight shifts that should have started yesterday, show warning all day today
                group.hasUnstartedShift = true;
              }
            }
          }
        }
      }
    });
    
    return { driverGroups: Array.from(driverGroups.values()), weekDays };
  };

  // Get shifts for a specific vehicle on a specific date
  const getShiftsForVehicle = (vehicleId, date) => {
    return getShiftsForDisplay().filter(shift => 
      shift.vehicleId === vehicleId && 
      (shift.displayDate === date || shift.date === date) &&
      shift.status !== 'cancelled' &&
      // For vehicle availability calculation, exclude end date displays of overnight shifts
      // to prevent double-counting the same shift on consecutive days
      !shift.isEndDateDisplay
    );
  };

  // Calculate vehicle availability for a specific day
  const calculateVehicleAvailability = (vehicle, date) => {
    const shifts = getShiftsForVehicle(vehicle.id, date);
    const availableSlots = [];
    const currentShiftDefinitions = SHIFT_DEFINITIONS[shiftDuration];
    
    // Sort shifts by start time
    const sortedShifts = shifts.sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
    
    // If no shifts, check availability for each shift type
    if (sortedShifts.length === 0) {
      Object.keys(currentShiftDefinitions).forEach(shiftType => {
        const shiftDef = currentShiftDefinitions[shiftType];
        if (shiftDef) {
          availableSlots.push({
            start: `${shiftDef.start.toString().padStart(2, '0')}:00`,
            end: `${shiftDef.end.toString().padStart(2, '0')}:00`,
            duration: shiftDef.overnight ? (24 - shiftDef.start + shiftDef.end) : (shiftDef.end - shiftDef.start),
            shiftType,
            isLongEnough: (shiftDef.overnight ? (24 - shiftDef.start + shiftDef.end) : (shiftDef.end - shiftDef.start)) >= (shiftDuration === '12h' ? 12 : 8),
            isFree: true
          });
        }
      });
    } else {
      // Calculate gaps between shifts
      let currentTime = 0; // Start of day (00:00)
      
      sortedShifts.forEach((shift, index) => {
        const shiftStart = shift.startTime.hour() + (shift.startTime.minute() / 60);
        const shiftEnd = shift.endTime.hour() + (shift.endTime.minute() / 60);
        
        // Check for gap before this shift
        if (shiftStart > currentTime) {
          const gapDuration = shiftStart - currentTime;
          if (gapDuration >= 1) { // At least 1 hour gap
            // Check which shift types this gap covers
            Object.keys(currentShiftDefinitions).forEach(shiftType => {
              const shiftDef = currentShiftDefinitions[shiftType];
              if (shiftDef) {
                const shiftTypeStart = shiftDef.start;
                const shiftTypeEnd = shiftDef.overnight ? shiftDef.end + 24 : shiftDef.end;
                
                // Check if gap overlaps with shift type
                const overlapStart = Math.max(currentTime, shiftTypeStart);
                const overlapEnd = Math.min(shiftStart, shiftTypeEnd);
                
                if (overlapEnd > overlapStart) {
                  const overlapDuration = overlapEnd - overlapStart;
                  if (overlapDuration >= 1) {
                    availableSlots.push({
                      start: `${Math.floor(overlapStart).toString().padStart(2, '0')}:${Math.round((overlapStart % 1) * 60).toString().padStart(2, '0')}`,
                      end: `${Math.floor(overlapEnd).toString().padStart(2, '0')}:${Math.round((overlapEnd % 1) * 60).toString().padStart(2, '0')}`,
                      duration: overlapDuration,
                      shiftType,
                      isLongEnough: overlapDuration >= (shiftDuration === '12h' ? 12 : 8),
                      isFree: true
                    });
                  }
                }
              }
            });
          }
        }
        
        currentTime = Math.max(currentTime, shiftEnd);
      });
      
      // Check for gap after last shift until end of day
      if (currentTime < 24) {
        const gapDuration = 24 - currentTime;
        if (gapDuration >= 1) {
          Object.keys(currentShiftDefinitions).forEach(shiftType => {
            const shiftDef = currentShiftDefinitions[shiftType];
            if (shiftDef) {
              const shiftTypeStart = shiftDef.start;
              const shiftTypeEnd = shiftDef.overnight ? shiftDef.end + 24 : shiftDef.end;
              
              // Check if gap overlaps with shift type
              const overlapStart = Math.max(currentTime, shiftTypeStart);
              // For overnight shifts, allow the end time to extend beyond 24:00
              const overlapEnd = shiftDef.overnight ? Math.min(shiftTypeEnd, currentTime + (24 - currentTime) + shiftDef.end) : Math.min(24, shiftTypeEnd);
              
              if (overlapEnd > overlapStart) {
                const overlapDuration = overlapEnd - overlapStart;
                if (overlapDuration >= 1) {
                  // Format the end time correctly for overnight shifts
                  let endHour = Math.floor(overlapEnd);
                  let endTimeStr;
                  if (endHour >= 24) {
                    endHour = endHour - 24;
                    endTimeStr = `${endHour.toString().padStart(2, '0')}:${Math.round((overlapEnd % 1) * 60).toString().padStart(2, '0')}`;
                  } else {
                    endTimeStr = `${endHour.toString().padStart(2, '0')}:${Math.round((overlapEnd % 1) * 60).toString().padStart(2, '0')}`;
                  }
                  
                  availableSlots.push({
                    start: `${Math.floor(overlapStart).toString().padStart(2, '0')}:${Math.round((overlapStart % 1) * 60).toString().padStart(2, '0')}`,
                    end: endTimeStr,
                    duration: overlapDuration,
                    shiftType,
                    isLongEnough: overlapDuration >= (shiftDuration === '12h' ? 12 : 8),
                    isFree: true,
                    isOvernight: shiftDef.overnight && overlapEnd > 24
                  });
                }
              }
            }
          });
        }
      }
    }
    
    // Remove duplicates and merge overlapping slots
    const uniqueSlots = [];
    availableSlots.forEach(slot => {
      const existing = uniqueSlots.find(s => 
        s.shiftType === slot.shiftType && 
        Math.abs(parseFloat(s.start.replace(':', '.')) - parseFloat(slot.start.replace(':', '.'))) < 0.1
      );
      if (!existing) {
        uniqueSlots.push(slot);
      }
    });
    
    return {
      vehicleId: vehicle.id,
      date,
      availableSlots: uniqueSlots,
      totalFreeHours: uniqueSlots.reduce((sum, slot) => sum + slot.duration, 0),
      longestFreeSlot: Math.max(...uniqueSlots.map(slot => slot.duration), 0)
    };
  };

  // Group vehicles by day for week view
  const getVehiclesGroupedByDay = () => {
    const weekDays = getWeekDays();
    const vehicleGroups = new Map();
    
    // Initialize groups for all vehicles
    vehicles.forEach(vehicle => {
      if (selectedDriver === 'all' || vehicle.id === selectedDriver) {
        const vehicleAvailability = new Map();
        weekDays.forEach(day => {
          const availability = calculateVehicleAvailability(vehicle, day.dateString);
          vehicleAvailability.set(day.dateString, availability);
        });
        
        vehicleGroups.set(vehicle.id, {
          vehicle,
          availability: vehicleAvailability
        });
      }
    });
    
    return { vehicleGroups: Array.from(vehicleGroups.values()), weekDays };
  };

  // Color definitions for vehicle availability
  const getAvailabilityColor = (slot) => {
    if (slot.duration >= 8) {
      return {
        bg: alpha(theme.palette.success.main, 0.2),
        border: theme.palette.success.main,
        text: theme.palette.success.main
      };
    } else if (slot.duration >= 4) {
      return {
        bg: alpha(theme.palette.warning.main, 0.2),
        border: theme.palette.warning.main,
        text: theme.palette.warning.main
      };
    } else {
      return {
        bg: alpha(theme.palette.error.main, 0.2),
        border: theme.palette.error.main,
        text: theme.palette.error.main
      };
    }
  };

  // Render vehicle availability for week view
  const renderVehicleAvailability = (vehicleGroup, day) => {
    const dayAvailability = vehicleGroup.availability.get(day.dateString);
    
    if (!dayAvailability || dayAvailability.availableSlots.length === 0) {
      return (
        <Box sx={{ 
          p: 0.5, 
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.grey[500], 0.1),
          borderRadius: 1,
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="caption" sx={{ 
            color: theme.palette.text.secondary,
            fontSize: '0.6rem'
          }}>
            Nicht verfügbar
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {dayAvailability.availableSlots.map((slot, index) => {
          const colors = getAvailabilityColor(slot);
          return (
            <Box
              key={index}
              sx={{
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: 1,
                padding: '2px 4px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(colors.border, 0.3)
                }
              }}
            >
              <Typography variant="caption" sx={{ 
                fontWeight: 'bold',
                fontSize: '0.65rem',
                display: 'block',
                color: colors.text,
                lineHeight: 1.1
              }}>
                {slot.start} - {slot.end}
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.55rem',
                color: theme.palette.text.secondary,
                display: 'block',
                lineHeight: 1
              }}>
                {Math.round(slot.duration)}h frei
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.5rem',
                color: theme.palette.text.secondary,
                display: 'block',
                lineHeight: 1
              }}>
                {SHIFT_DEFINITIONS[shiftDuration][slot.shiftType]?.label || slot.shiftType}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Render shift for week view (compact format)
  const renderShiftForWeek = (shift) => {
    const isActive = shift.actualShift !== null;
    const hasConflict = isActive && shift.actualShift.currentShiftNumber > 1;
    const isUnplanned = shift.status === 'unplanned';
    
    // Check if shift should have started but hasn't
    const now = dayjs();
    const isPlannedButNotStarted = !isActive && shift.startTime.isBefore(now) && !isUnplanned;
    
    // Extract actual shift times if available
    let actualStartTime = null;
    let actualEndTime = null;
    let isActuallyStarted = false;
    let isActuallyEnded = false;
    let startedEarly = false;
    let startedLate = false;
    let endedEarly = false;
    let endedLate = false;
    let startTimeDiff = 0;
    let endTimeDiff = 0;
    
    if (isActive && shift.actualShift) {
      let activeShift = shift.actualShift.shifts?.find(s => s.isActive);
      
      if (!activeShift && shift.actualShift.shifts && shift.actualShift.shifts.length > 0) {
        activeShift = shift.actualShift.shifts[shift.actualShift.shifts.length - 1];
      }
      
      if (activeShift) {
        if (activeShift.startTime) {
          actualStartTime = dayjs(activeShift.startTime);
          isActuallyStarted = true;
        } else if (activeShift.shiftStart) {
          const timestamp = activeShift.shiftStart;
          if (timestamp && timestamp.seconds) {
            actualStartTime = dayjs.unix(timestamp.seconds);
            isActuallyStarted = true;
          }
        }
        
        if (actualStartTime) {
          startTimeDiff = actualStartTime.diff(shift.startTime, 'minute');
          startedEarly = startTimeDiff < -5;
          startedLate = startTimeDiff > 5;
        }
        
        if (activeShift.endTime) {
          actualEndTime = dayjs(activeShift.endTime);
          isActuallyEnded = true;
        } else if (activeShift.shiftEnd && activeShift.shiftEnd.seconds) {
          actualEndTime = dayjs.unix(activeShift.shiftEnd.seconds);
          isActuallyEnded = true;
        }
        
        if (actualEndTime && isActuallyEnded) {
          endTimeDiff = actualEndTime.diff(shift.endTime, 'minute');
          endedEarly = endTimeDiff < -5;
          endedLate = endTimeDiff > 5;
        }
      }
    }
    
    // Find vehicle information
    const vehicle = shift.vehicleId ? vehicles.find(v => v.id === shift.vehicleId) : null;
    
    return (
      <Box
        key={`${shift.id}-${shift.date}`}
        sx={{
          backgroundColor: shift.isEndDateDisplay 
            ? alpha(theme.palette.grey[400], 0.2) // Gray background for end date displays
            : isUnplanned ? alpha(theme.palette.warning.main, 0.2) : isActive ? alpha(theme.palette.success.main, 0.2) : isPlannedButNotStarted ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.primary.main, 0.2),
          border: `1px solid ${shift.isEndDateDisplay 
            ? theme.palette.grey[400] // Gray border for end date displays
            : isUnplanned ? theme.palette.warning.main : isActive ? theme.palette.success.main : isPlannedButNotStarted ? theme.palette.error.main : theme.palette.primary.main}`,
          borderRadius: 1,
          padding: '2px 4px',
          cursor: shift.isEndDateDisplay ? 'default' : 'pointer', // No pointer cursor for end date displays
          mb: 0.25,
          position: 'relative',
          opacity: shift.isEndDateDisplay ? 0.7 : 1, // Slightly transparent for end date displays
          '&:hover': shift.isEndDateDisplay ? {} : { // No hover effect for end date displays
            backgroundColor: isUnplanned ? alpha(theme.palette.warning.main, 0.3) : isActive ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.primary.main, 0.3),
            zIndex: 10
          }
        }}
        onClick={shift.isEndDateDisplay ? undefined : () => handleEditShift(shift.id)} // No click handler for end date displays
      >
        {/* Context menu button - only show for clickable shifts */}
        {!shift.isEndDateDisplay && (
          <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 20 }}>
            <IconButton
              size="small"
              onClick={(e) => handleContextMenu(e, shift)}
              sx={{ 
                width: 14, 
                height: 14, 
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': { backgroundColor: alpha(theme.palette.background.paper, 0.9) }
              }}
            >
              <MoreVertIcon sx={{ fontSize: 10 }} />
            </IconButton>
          </Box>
        )}
        
        <Box sx={{ pr: 1.5 }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 'bold',
            color: isActuallyStarted ? theme.palette.text.secondary : 'inherit',
            textDecoration: isActuallyStarted ? 'line-through' : 'none',
            display: 'block',
            fontSize: '0.7rem',
            lineHeight: 1.2
          }}>
            {(() => {
              if (shift.isOvernightShift) {
                let originalStartDateStr, originalStartTimeStr;
                
                if (shift.isEndDateDisplay) {
                  originalStartDateStr = Object.keys(shift.dateEndMapping || {}).find(
                    startDate => shift.dateEndMapping[startDate] === shift.displayDate
                  );
                  originalStartTimeStr = shift.originalStartTimeString;
                } else {
                  originalStartDateStr = shift.date;
                  originalStartTimeStr = shift.originalStartTimeString;
                }
                
                if (originalStartDateStr && originalStartTimeStr) {
                  const [hours, minutes] = originalStartTimeStr.split(':');
                  const originalStartTime = dayjs(originalStartDateStr).hour(parseInt(hours)).minute(parseInt(minutes));
                  const originalEndTime = originalStartTime.add(shift.plannedDuration, 'hour');
                  return `${originalStartTime.format('HH:mm')} - ${originalEndTime.format('HH:mm')} ${shift.isEndDateDisplay ? '(-1)' : '(+1)'}`;
                }
              }
              return `${shift.startTime.format('HH:mm')} - ${shift.endTime.format('HH:mm')}${shift.isOvernightShift ? (shift.isEndDateDisplay ? ' (-1)' : ' (+1)') : ''}`;
            })()}
            {isUnplanned && ' (ungeplant)'}
          </Typography>
          
          {vehicle && (
            <Box sx={{ mt: 0.25 }}>
              <Typography variant="caption" sx={{ 
                fontSize: '0.55rem', 
                color: theme.palette.text.secondary,
                lineHeight: 1,
                display: 'block'
              }}>
                {vehicle.model || 'Unbekanntes Modell'}
              </Typography>
              <Typography variant="caption" sx={{ 
                fontSize: '0.55rem', 
                color: theme.palette.text.secondary,
                lineHeight: 1,
                display: 'block'
              }}>
                {vehicle.licensePlate || 'Unbekannt'}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
            {hasConflict && (
              <Tooltip title="Mehrere Schichten am Tag">
                <WarningIcon sx={{ fontSize: 8, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {hasMultipleDates(shift) && (
              <Tooltip title={`Mehrtägige Schicht (${shift.dates.length} Tage)`}>
                <Chip 
                  label={shift.dates.length}
                  size="small"
                  sx={{ 
                    fontSize: '0.4rem', 
                    height: 10,
                    backgroundColor: alpha(theme.palette.info.main, 0.2),
                    color: theme.palette.info.main,
                    '& .MuiChip-label': {
                      px: 0.2
                    }
                  }}
                />
              </Tooltip>
            )}
            {startedEarly && (
              <Tooltip title={`${Math.abs(startTimeDiff)} Min. früher`}>
                <EarlyIcon sx={{ fontSize: 8, color: theme.palette.info.main }} />
              </Tooltip>
            )}
            {startedLate && (
              <Tooltip title={`${startTimeDiff} Min. später`}>
                <LateIcon sx={{ fontSize: 8, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {endedEarly && (
              <Tooltip title={`${Math.abs(endTimeDiff)} Min. früher beendet`}>
                <EarlyIcon sx={{ fontSize: 8, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {endedLate && (
              <Tooltip title={`${endTimeDiff} Min. später beendet`}>
                <LateIcon sx={{ fontSize: 8, color: theme.palette.error.main }} />
              </Tooltip>
            )}
          </Box>
          
          {isActuallyStarted && (
            <Typography variant="caption" sx={{ 
              color: theme.palette.success.main,
              fontWeight: 'bold',
              fontSize: '0.6rem',
              display: 'block',
              lineHeight: 1
            }}>
              {actualStartTime.format('HH:mm')} - {actualEndTime ? actualEndTime.format('HH:mm') : 'läuft'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };
  
  const renderShift = (shift) => {
    const isActive = shift.actualShift !== null;
    const hasConflict = isActive && shift.actualShift.currentShiftNumber > 1;
    const isUnplanned = shift.status === 'unplanned';
    
    // Check if shift should have started but hasn't
    const now = dayjs();
    const isPlannedButNotStarted = !isActive && shift.startTime.isBefore(now) && !isUnplanned;
    
    // Extract actual shift times if available
    let actualStartTime = null;
    let actualEndTime = null;
    let isActuallyStarted = false;
    let isActuallyEnded = false;
    let startedEarly = false;
    let startedLate = false;
    let endedEarly = false;
    let endedLate = false;
    let startTimeDiff = 0;
    let endTimeDiff = 0;
    
    if (isActive && shift.actualShift) {
      // Try to find active shift in the shifts array
      let activeShift = shift.actualShift.shifts?.find(s => s.isActive);
      
      // If no active shift found, look for the most recent shift (ended shifts)
      if (!activeShift && shift.actualShift.shifts && shift.actualShift.shifts.length > 0) {
        // Get the last shift (most recent) regardless of isActive status
        activeShift = shift.actualShift.shifts[shift.actualShift.shifts.length - 1];
        console.log('Found ended shift data:', activeShift);
      }
      
      if (activeShift) {
        console.log('Processing shift data:', activeShift);
        
        // Handle both possible timestamp formats
        if (activeShift.startTime) {
          actualStartTime = dayjs(activeShift.startTime);
          isActuallyStarted = true;
        } else if (activeShift.shiftStart) {
          // Convert Firebase Timestamp to dayjs
          const timestamp = activeShift.shiftStart;
          if (timestamp && timestamp.seconds) {
            actualStartTime = dayjs.unix(timestamp.seconds);
            isActuallyStarted = true;
          }
        }
        
        if (actualStartTime) {
          // Compare actual vs planned start time
          startTimeDiff = actualStartTime.diff(shift.startTime, 'minute');
          startedEarly = startTimeDiff < -5; // Started more than 5 minutes early
          startedLate = startTimeDiff > 5; // Started more than 5 minutes late
        }
        
        // Handle endTime/shiftEnd
        if (activeShift.endTime) {
          actualEndTime = dayjs(activeShift.endTime);
          isActuallyEnded = true;
        } else if (activeShift.shiftEnd && activeShift.shiftEnd.seconds) {
          actualEndTime = dayjs.unix(activeShift.shiftEnd.seconds);
          isActuallyEnded = true;
        }
        
        // Compare actual vs planned end time
        if (actualEndTime && isActuallyEnded) {
          endTimeDiff = actualEndTime.diff(shift.endTime, 'minute');
          endedEarly = endTimeDiff < -5; // Ended more than 5 minutes early
          endedLate = endTimeDiff > 5; // Ended more than 5 minutes late
          
          console.log('End time comparison:', {
            planned: shift.endTime.format('HH:mm'),
            actual: actualEndTime.format('HH:mm'),
            diff: endTimeDiff,
            endedEarly,
            endedLate
          });
        }
      } else {
        // Fallback: check if actualShift has direct properties
        if (shift.actualShift.startTime) {
          actualStartTime = dayjs(shift.actualShift.startTime);
          isActuallyStarted = true;
          
          const timeDiff = actualStartTime.diff(shift.startTime, 'minute');
          startedEarly = timeDiff < -5;
          startedLate = timeDiff > 5;
        }
        if (shift.actualShift.endTime) {
          actualEndTime = dayjs(shift.actualShift.endTime);
          isActuallyEnded = true;
        }
      }
    }
    
    // Find vehicle information
    const vehicle = shift.vehicleId ? vehicles.find(v => v.id === shift.vehicleId) : null;
    const vehicleInfo = vehicle ? `${vehicle.licensePlate || 'Unbekannt'} - ${vehicle.model || 'Unbekanntes Modell'}` : null;
    
    return (
      <Box
        key={`${shift.id}-${shift.date}`}
        sx={{
          position: 'absolute',
          ...calculateShiftStyle(shift, isPlannedButNotStarted),
          backgroundColor: shift.isEndDateDisplay 
            ? alpha(theme.palette.grey[400], 0.2) // Gray background for end date displays
            : isUnplanned ? alpha(theme.palette.warning.main, 0.2) : isActive ? alpha(theme.palette.success.main, 0.2) : isPlannedButNotStarted ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.primary.main, 0.2),
          border: `2px solid ${shift.isEndDateDisplay 
            ? theme.palette.grey[400] // Gray border for end date displays
            : isUnplanned ? theme.palette.warning.main : isActive ? theme.palette.success.main : isPlannedButNotStarted ? theme.palette.error.main : theme.palette.primary.main}`,
          borderRadius: 1,
          padding: '4px 8px',
          cursor: shift.isEndDateDisplay ? 'default' : 'pointer', // No pointer cursor for end date displays
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'hidden',
          opacity: shift.isEndDateDisplay ? 0.7 : 1, // Slightly transparent for end date displays
          '&:hover': shift.isEndDateDisplay ? {} : { // No hover effect for end date displays
            backgroundColor: isUnplanned ? alpha(theme.palette.warning.main, 0.3) : isActive ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.primary.main, 0.3),
            zIndex: 10
          }
        }}
        onClick={shift.isEndDateDisplay ? undefined : () => handleEditShift(shift.id)} // No click handler for end date displays
      >
        {/* Context menu button - only show for clickable shifts */}
        {!shift.isEndDateDisplay && (
          <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 20 }}>
            <IconButton
              size="small"
              onClick={(e) => handleContextMenu(e, shift)}
              sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': { backgroundColor: alpha(theme.palette.background.paper, 0.9) }
              }}
            >
              <MoreVertIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0, width: '100%', pr: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography variant="caption" noWrap sx={{ 
                color: isActuallyStarted ? theme.palette.text.secondary : 'inherit',
                textDecoration: isActuallyStarted ? 'line-through' : 'none'
              }}>
                Geplant: {(() => {
                  // For overnight shifts, always show original start and end times without midnight interruption
                  if (shift.isOvernightShift) {
                    // For both start and end date displays, show the original times
                    let originalStartDateStr, originalStartTimeStr;
                    
                    if (shift.isEndDateDisplay) {
                      // Find the original start date for this end date
                      originalStartDateStr = Object.keys(shift.dateEndMapping || {}).find(
                        startDate => shift.dateEndMapping[startDate] === shift.displayDate
                      );
                      originalStartTimeStr = shift.originalStartTimeString;
                    } else {
                      // For start date displays, use the shift's own date and time
                      originalStartDateStr = shift.date;
                      originalStartTimeStr = shift.originalStartTimeString;
                    }
                    
                    if (originalStartDateStr && originalStartTimeStr) {
                      const [hours, minutes] = originalStartTimeStr.split(':');
                      const originalStartTime = dayjs(originalStartDateStr).hour(parseInt(hours)).minute(parseInt(minutes));
                      const originalEndTime = originalStartTime.add(shift.plannedDuration, 'hour');
                      return `${originalStartTime.format('HH:mm')} - ${originalEndTime.format('HH:mm')} ${shift.isEndDateDisplay ? '(-1 Tag)' : '(+1 Tag)'}`;
                    }
                  }
                  return `${shift.startTime.format('HH:mm')} - ${shift.endTime.format('HH:mm')}${shift.isOvernightShift ? (shift.isEndDateDisplay ? ' (-1 Tag)' : ' (+1 Tag)') : ''}`;
                })()}
                {isUnplanned && ' (ungeplant)'}
              </Typography>
              {vehicleInfo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                  <VehicleIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" noWrap sx={{ 
                    fontSize: '0.6rem', 
                    color: theme.palette.text.secondary,
                    maxWidth: '180px'
                  }}>
                    {vehicleInfo}
                  </Typography>
                </Box>
              )}
            </Box>
            {isActuallyStarted && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" noWrap sx={{ 
                    color: theme.palette.success.main,
                    fontWeight: 'bold'
                  }}>
                    Start: {actualStartTime.format('HH:mm')}
                  </Typography>
                  {startedEarly && (
                    <Tooltip title={`Schicht ${Math.abs(startTimeDiff)} Minuten früher begonnen`}>
                      <EarlyIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.info.main,
                        transform: 'rotate(45deg)'
                      }} />
                    </Tooltip>
                  )}
                  {startedLate && (
                    <Tooltip title={`Schicht ${startTimeDiff} Minuten später begonnen`}>
                      <LateIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.warning.main,
                        transform: 'rotate(45deg)'
                      }} />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" noWrap sx={{ 
                    color: isActuallyEnded 
                      ? (endedEarly ? theme.palette.warning.main : endedLate ? theme.palette.error.main : theme.palette.success.main)
                      : theme.palette.text.secondary,
                    fontWeight: 'bold'
                  }}>
                    Ende: {actualEndTime ? actualEndTime.format('HH:mm') : 'läuft'}
                  </Typography>
                  {endedEarly && (
                    <Tooltip title={`Schicht ${Math.abs(endTimeDiff)} Minuten früher beendet`}>
                      <EarlyIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.warning.main,
                        transform: 'rotate(-45deg)'
                      }} />
                    </Tooltip>
                  )}
                  {endedLate && (
                    <Tooltip title={`Schicht ${endTimeDiff} Minuten später beendet`}>
                      <LateIcon sx={{ 
                        fontSize: 12, 
                        color: theme.palette.error.main,
                        transform: 'rotate(-45deg)'
                      }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {hasConflict && (
              <Tooltip title="Mehrere Schichten am Tag">
                <WarningIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {hasMultipleDates(shift) && (
              <Tooltip title={`Mehrtägige Schicht (${shift.dates.length} Tage)`}>
                <Chip 
                  label={shift.dates.length}
                  size="small"
                  sx={{ 
                    fontSize: '0.6rem', 
                    height: 16,
                    backgroundColor: alpha(theme.palette.info.main, 0.2),
                    color: theme.palette.info.main,
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              </Tooltip>
            )}
            {viewMode === 'week' && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                {dayjs(shift.date).format('dd')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };
  
  const renderFleetUtilization = () => {
    const utilization = calculateFleetUtilization();
    
    return (
      <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Flottenauslastung
        </Typography>
        <Box sx={{ position: 'relative', height: 40, mt: 1 }}>
          {utilization.map((slot, index) => (
            <Tooltip
              key={slot.hour}
              title={`${slot.hour}:00 - ${slot.utilized}/${slot.total} Fahrzeuge (${Math.round(slot.percent)}%)`}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: `${index * (HOUR_WIDTH / (getTimeSlots().length - 1)) * 100}%`,
                  width: `${100 / (getTimeSlots().length - 1)}%`,
                  height: '100%',
                  backgroundColor: 
                    slot.percent >= 80 ? theme.palette.error.main :
                    slot.percent >= 60 ? theme.palette.warning.main :
                    slot.percent >= 40 ? theme.palette.success.main :
                    theme.palette.grey[300],
                  opacity: 0.6,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            </Tooltip>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Unterbesetzt (&lt;40%)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vollbesetzt (&gt;80%)
          </Typography>
        </Box>
      </Paper>
    );
  };
  
  if (isLoading) {
    return <Loading message="Schichtplan wird geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Schichtplans"
        message={error}
        onRetry={() => dispatch(fetchPlannedShifts())}
      />
    );
  }
  
  const driverGroups = groupShiftsByDriver();
  const timeSlots = getTimeSlots();
  
  // Get data for week view
  const { driverGroups: weekDriverGroups, weekDays } = getShiftsGroupedByDriverAndDay();
  const { vehicleGroups: weekVehicleGroups } = displayMode === 'vehicles' ? getVehiclesGroupedByDay() : { vehicleGroups: [] };
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Schichtplaner"
        description="Planung und Übersicht aller geplanten Schichten"
        actionButton={{
          label: 'Schicht hinzufügen',
          icon: <AddIcon />,
          path: '/schichtplaner/neu'
        }}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichtplaner', path: '/schichtplaner' }
        ]}
      />
      
      {/* Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              fullWidth
            >
              <ToggleButton value="day">
                <TodayIcon sx={{ mr: 1 }} />
                Tag
              </ToggleButton>
              <ToggleButton value="week">
                <WeekIcon sx={{ mr: 1 }} />
                Woche
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          
          {viewMode === 'week' && (
            <Grid size={{ xs: 12, md: 2 }}>
              <ToggleButtonGroup
                value={displayMode}
                exclusive
                onChange={handleDisplayModeChange}
                size="small"
                fullWidth
              >
                <ToggleButton value="drivers">
                  <PersonIcon sx={{ mr: 1 }} />
                  Fahrer
                </ToggleButton>
                <ToggleButton value="vehicles">
                  <VehicleIcon sx={{ mr: 1 }} />
                  Fahrzeuge
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}
          
          <Grid size={{ xs: 12, md: displayMode === 'vehicles' && viewMode === 'week' ? 2.5 : 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{viewMode === 'week' && displayMode === 'vehicles' ? 'Fahrzeuge' : 'Fahrer'}</InputLabel>
              <Select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                label={viewMode === 'week' && displayMode === 'vehicles' ? 'Fahrzeuge' : 'Fahrer'}
              >
                <MenuItem value="all">{viewMode === 'week' && displayMode === 'vehicles' ? 'Alle Fahrzeuge' : 'Alle Fahrer'}</MenuItem>
                {viewMode === 'week' && displayMode === 'vehicles' ? (
                  vehicles && vehicles.length > 0 ? (
                    vehicles.map(vehicle => (
                      <MenuItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.licensePlate || 'Unbekannt'} - {vehicle.model || 'Unbekanntes Modell'}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Keine Fahrzeuge verfügbar</MenuItem>
                  )
                ) : (
                  drivers && drivers.length > 0 ? (
                    drivers.map(driver => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.user?.name || driver.name || 'Unbekannter Fahrer'}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Keine Fahrer verfügbar</MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
          
          {displayMode === 'vehicles' && viewMode === 'week' && (
            <>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <ToggleButtonGroup
                  value={shiftDuration}
                  exclusive
                  onChange={handleShiftDurationChange}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="8h">
                    8h
                  </ToggleButton>
                  <ToggleButton value="12h">
                    12h
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </>
          )}
          
          <Grid size={{ xs: 12, md: displayMode === 'vehicles' && viewMode === 'week' ? 4 : viewMode === 'week' ? 5 : 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <IconButton onClick={() => handleDateChange('prev')}>
                <PrevIcon />
              </IconButton>
              
              <Chip
                icon={<CalendarIcon />}
                label={
                  viewMode === 'week'
                    ? `KW ${currentDate.isoWeek()} (${currentDate.startOf('isoWeek').format('DD.MM.')}-${currentDate.endOf('isoWeek').format('DD.MM.')})`
                    : currentDate.format('dddd, DD.MM.YYYY')
                }
                onClick={handleToday}
              />
              
              <IconButton onClick={() => handleDateChange('next')}>
                <NextIcon />
              </IconButton>
              
              {isToday && viewMode === 'day' && (
                <Chip
                  icon={<TimeIcon />}
                  label={isRollingView ? '24h Rolling' : 'Feste Zeiten'}
                  variant={isRollingView ? 'filled' : 'outlined'}
                  onClick={() => setIsRollingView(!isRollingView)}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Shift Table */}
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        {viewMode === 'week' ? (
          /* Week View - Day Columns */
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ 
              minWidth: 180 + (weekDays.length * 180), // Driver column + day columns
              width: 'max-content'
            }}>
              {/* Day Headers */}
              <Box sx={{ 
                display: 'flex',
                height: HEADER_HEIGHT,
                borderBottom: `2px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 100
              }}>
                {/* Driver/Vehicle Column Header */}
                <Box sx={{
                  width: 180,
                  flexShrink: 0,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2
                }}>
                  <Typography variant="subtitle2">
                    {displayMode === 'vehicles' ? 'Fahrzeuge' : 'Fahrer'}
                  </Typography>
                </Box>
                
                {/* Day Column Headers */}
                {weekDays.map((day) => (
                  <Box
                    key={day.dateString}
                    sx={{
                      width: 180,
                      minWidth: 180,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: day.isToday ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      position: 'relative',
                      px: 1
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: day.isToday ? 'bold' : 'normal',
                        color: day.isToday ? theme.palette.primary.main : 'inherit'
                      }}
                    >
                      {day.dayName}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.7rem',
                        color: day.isToday ? theme.palette.primary.main : theme.palette.text.secondary
                      }}
                    >
                      {day.dayNumber}
                    </Typography>
                    {day.isToday && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          backgroundColor: theme.palette.primary.main
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
              
              {/* Driver/Vehicle Rows */}
              {displayMode === 'vehicles' ? (
                /* Vehicle Rows */
                weekVehicleGroups.map((group, groupIndex) => (
                  <Box
                    key={group.vehicle.id}
                    sx={{
                      display: 'flex',
                      minHeight: ROW_HEIGHT,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: groupIndex % 2 === 1 ? alpha(theme.palette.action.hover, 0.1) : 'transparent'
                    }}
                  >
                    {/* Vehicle Info Column */}
                    <Box sx={{
                      width: 180,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {group.vehicle.licensePlate || 'Unbekannt'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.vehicle.model || 'Unbekanntes Modell'}
                          </Typography>
                        </Box>
                        <VehicleIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                      </Box>
                    </Box>
                    
                    {/* Day Columns */}
                    {weekDays.map((day) => (
                      <Box
                        key={`${group.vehicle.id}-${day.dateString}`}
                        sx={{
                          width: 180,
                          minWidth: 180,
                          flexShrink: 0,
                          borderRight: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {renderVehicleAvailability(group, day)}
                      </Box>
                    ))}
                  </Box>
                ))
              ) : (
                /* Driver Rows */
                weekDriverGroups.map((group, groupIndex) => (
                  <Box
                    key={group.driver.id}
                    sx={{
                      display: 'flex',
                      minHeight: ROW_HEIGHT,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: groupIndex % 2 === 1 ? alpha(theme.palette.action.hover, 0.1) : 'transparent'
                    }}
                  >
                    {/* Driver Info Column */}
                    <Box sx={{
                      width: 180,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {group.driver.user?.name || group.driver.name || 'Unbekannter Fahrer'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.driver.driverNumber ? `Nr. ${group.driver.driverNumber}` : 'Fahrer'}
                          </Typography>
                        </Box>
                        {group.hasUnstartedShift && (
                          <Tooltip title="Schicht nicht angetreten">
                            <WarningIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Day Columns */}
                    {weekDays.map((day) => (
                      <Box
                        key={`${group.driver.id}-${day.dateString}`}
                        sx={{
                          width: 180,
                          minWidth: 180,
                          flexShrink: 0,
                          borderRight: `1px solid ${theme.palette.divider}`,
                          p: 0.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.25
                        }}
                      >
                        {group.shifts.get(day.dateString)?.map(shift => renderShiftForWeek(shift)) || []}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
              
              {/* Empty state */}
              {(displayMode === 'vehicles' ? weekVehicleGroups.length === 0 : weekDriverGroups.length === 0) && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {displayMode === 'vehicles' 
                      ? 'Keine Fahrzeuge für den ausgewählten Zeitraum gefunden'
                      : 'Keine Fahrer oder Schichten für den ausgewählten Zeitraum gefunden'
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          /* Day View - Timeline */
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ 
              width: timeSlots.length * HOUR_WIDTH + 150, 
              minWidth: timeSlots.length * HOUR_WIDTH + 150,
              position: 'relative' 
            }}>
              {/* Background stripes for better row distinction */}
              {driverGroups.map((group, groupIndex) => (
                groupIndex % 2 === 1 && (
                  <Box
                    key={`stripe-${group.driver.id}`}
                    sx={{
                      position: 'absolute',
                      top: HEADER_HEIGHT + (groupIndex * ROW_HEIGHT),
                      left: 0,
                      right: 0,
                      width: '100%',
                      height: ROW_HEIGHT,
                      backgroundColor: alpha(theme.palette.action.hover, 0.3),
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                )
              ))}
              {/* Current Time Marker */}
              <Box sx={getCurrentTimeMarkerStyle()} />
              {/* Time Header */}
              <Box
                sx={{
                  display: 'flex',
                  height: HEADER_HEIGHT,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  position: 'sticky',
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 100
                }}
              >
                <Box
                  sx={{
                    width: 150,
                    flexShrink: 0,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2
                  }}
                >
                  <Typography variant="subtitle2">Fahrer</Typography>
                </Box>
                {timeSlots.map((slot, index) => (
                  <Box
                    key={`${slot.hour}-${index}`}
                    sx={{
                      width: HOUR_WIDTH,
                      minWidth: HOUR_WIDTH,
                      maxWidth: HOUR_WIDTH,
                      flexShrink: 0,
                      borderRight: index < timeSlots.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: slot.isCurrent ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      position: 'relative'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: slot.isCurrent ? 'bold' : 'normal',
                        color: slot.isCurrent ? theme.palette.primary.main : 'inherit'
                      }}
                    >
                      {slot.label}
                    </Typography>
                    {slot.isCurrent && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          backgroundColor: theme.palette.primary.main
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
              
              {/* Driver Rows */}
              {driverGroups.map((group) => (
                <Box
                  key={group.driver.id}
                  sx={{
                    display: 'flex',
                    height: ROW_HEIGHT,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 150,
                      flexShrink: 0,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {group.driver.user?.name || group.driver.name || 'Unbekannter Fahrer'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.driver.driverNumber ? `Nr. ${group.driver.driverNumber}` : 'Fahrer'}
                        </Typography>
                      </Box>
                      {group.hasUnstartedShift && (
                        <Tooltip title="Schicht nicht angetreten">
                          <WarningIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      flex: 1,
                      width: `${timeSlots.length * HOUR_WIDTH}px`
                    }}
                  >
                    {group.shifts.map(shift => renderShift(shift))}
                  </Box>
                </Box>
              ))}
              
              {/* Empty state */}
              {driverGroups.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Fahrer oder Schichten für den ausgewählten Zeitraum gefunden
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Paper>
      
      {/* Fleet Utilization Timeline */}
      {viewMode === 'day' && renderFleetUtilization()}
      
      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
        slotProps={{ paper: {
          style: {
            maxHeight: 48 * 4.5,
            width: '20ch',
          },
        }}}
      >
        <MenuList>
          <MenuItem onClick={handleEditFromMenu}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bearbeiten</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteFromMenu}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Löschen</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Schicht löschen
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Welche Schichten möchten Sie löschen?
          </Typography>
          
          
          {selectedShift && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Fahrer:</strong> {selectedShift?.driverName || 'Unbekannt'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Zeit:</strong> {selectedShift?.originalStartTimeString || selectedShift?.startTime || 'Unbekannt'} - {selectedShift?.plannedDuration || 0}h
                </Typography>
                {hasMultipleDates(selectedShift) && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tage:</strong> {getOriginalDates(selectedShift).length} Tage ({getOriginalDates(selectedShift).join(', ')})
                  </Typography>
                )}
              </Box>
              
              <RadioGroup
                value={deleteOption}
                onChange={(e) => setDeleteOption(e.target.value)}
                sx={{ mt: 2 }}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        Nur diesen Tag ({selectedShift?.displayDate || selectedShift?.date})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hasMultipleDates(selectedShift) 
                          ? 'Andere Tage der Schicht bleiben bestehen'
                          : 'Nur diesen einzelnen Tag löschen'
                        }
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {hasMultipleDates(selectedShift) 
                          ? `Alle Tage dieser Schicht (${getOriginalDates(selectedShift).length} Tage)`
                          : 'Komplette Schicht'
                        }
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hasMultipleDates(selectedShift)
                          ? 'Die gesamte Schicht wird gelöscht'
                          : 'Die Schicht wird vollständig gelöscht'
                        }
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftPlanner;